import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    BUILDER_CHART_VIEW_TYPES,
    FavoriteLimits,
    PerpsAssetsData,
    PerpsBreakdownByCohorts,
    PerpsBreakdownBySize,
    PositionMetricsData,
    Positions,
    RANGE_TYPES,
    SegmentId,
    SegmentMetricsData,
    SegmentsBiasMetrics,
    SegmentsData,
    StartTotalWalletsEquity,
    StatsActivePerpTraders,
    StatsEquityDistributionBuckets,
    StatsExposeRatio,
    StatsMetricByHype,
    StatsMetrics,
    StatsTotalWalletsOnHL,
    StatsWalletCreationRate,
} from '@/lib/types/hypertrackerTypes';
import {
    HypertrackerState,
    LiquidationHeatmapItem,
    PositionExplorerFilters,
    VaultConnection,
    WalletConnection,
    WalletsParams,
    Revenue,
    TopBuildersRevenue,
    UsersForBuilder,
    BuilderUser,
    BuildersByPeriod,
    BuilderV2Personal,
    HypertrackerLeaderboards,
    LeaderboardsTableFilters,
    LEADERBOARDS,
    HypertrackerLeaderboardData,
} from '@/state/hypertracker/hypertrackerTypes';

const initialState: HypertrackerState = {
    segmentData: null,
    segmentsSummary: null,
    segmentsBiasMetrics: null,
    singleSegmentSummary: {},
    largestPositions: {},
    wallets: {},
    liquidationRisk: {},
    filters: {
        largestPositions: {
            limit: 50,
            offset: 0,
        },
        wallets: {
            limit: 50,
            offset: 0,
            order: 'desc',
            orderBy: 'openValue',
            pnlSegmentIds: [],
            sizeSegmentIds: [],
            hasOpenPositions: undefined,
        },
        liquidationRisk: {
            limit: 50,
            offset: 0,
        },
    },
    modalWindow: {
        modalType: null,
        isTwitterData: false,
        xUser: null,
        benefitModalType: null,
        authorizationModalType: null,
        wallet: '',
        twitterConnectUrl: '',
        positionId: '',
        editDisplayedNameForFavorites: null,
        vaultInfoModalDetails: null,
    },
    isLoadingDisplayX: false,
    heatmapCellDetails: null,
    heatmapSelectedRow: null,
    heatmapSelectedField: null,
    tempKeyData: null,
    connections: [],
    vaults: [],
    favoritePositions: [],
    favoriteAccounts: [],
    favoriteIdsHolderObject: null,
    featuredAccounts: [],
    featuredPositions: [],
    favoriteLimits: {
        minPerpMonthVolume: 0,
        perpMonthVolume: 0,
        countFavorites: 0,
        unlockedAccounts: 0,
        totalAccounts: 0,
        maxFavorites: 0,
    },
    perpsCurrentAsset: 'BTC',
    perpsAssetsData: null,
    perpsPositionsToExplore: [],
    positionExplorerFilters: {
        coin: '',
        limit: 10,
        offset: 0,
        order: 'desc',
        orderBy: 'coin',
        segmentIds: [],
        valueRange: [],
    },
    buildersDailyAnalytics: null,
    buildersAllTimeAnalytics: null,
    buildersAllTime: null,
    builders24h: null,
    buildersByPeriod: null,
    topBuildersRevenue: null,
    builderV2: null,
    usersForBuilder: null,
    builderMainChartView: null,
    buildersTableRange: RANGE_TYPES.MONTH,
    personalBuilderRange: null,
    statsMetrics: null,
    statsExposeRatio: null,
    statsMetricByHype: null,
    statsActivePerpTraders: null,
    statsTotalWalletsOnHL: null,
    statsWalletCreationRate: null,
    startTotalWalletsEquity: null,
    statsEquityDistributionBuckets: null,
    leaderboards: null,
    leaderboardsTableFilters: {
        coin: '',
        limit: 50,
        offset: 0,
        order: 'desc',
        orderBy: 'coin',
        segmentIds: [],
        valueRange: [],
        rankBy: 'pnlAllTime',
    },
};

const slice = createSlice({
    name: 'hypertracker',
    initialState,
    reducers: {
        setSegmentsSummary: (state, action: PayloadAction<SegmentsData>) => {
            state.segmentsSummary = action.payload;
        },
        setSegmentsBiasMetrics: (state, action: PayloadAction<SegmentsBiasMetrics>) => {
            state.segmentsBiasMetrics = action.payload;
        },

        setSegmentSummaryById: (state, action: PayloadAction<{ segmentId: number; data: any }>) => {
            const { segmentId, data } = action.payload;
            state.singleSegmentSummary[segmentId] = data;
        },
        setLargestPositions: (
            state,
            action: PayloadAction<{ segmentId: number; data: any; totalCount: number }>,
        ) => {
            const { segmentId, data, totalCount } = action.payload;
            state.largestPositions[segmentId] = { items: data, totalCount };
        },
        setWallets: (
            state,
            action: PayloadAction<{ segmentId: number; data: any; totalCount: number }>,
        ) => {
            const { segmentId, data, totalCount } = action.payload;
            state.wallets[segmentId] = { items: data, totalCount };
        },

        updateWallets: (state, action: PayloadAction<{ segmentId: SegmentId; row: any }>) => {
            const { segmentId, row } = action.payload;
            const index = state.wallets[segmentId]?.items?.findIndex(
                (item) => item.address === row.address,
            );

            if (index !== -1 && state.wallets[segmentId]?.items) {
                state.wallets[segmentId].items?.splice(index, 1, row);
            }
        },

        setLiquidationRisk: (
            state,
            action: PayloadAction<{ segmentId: number; data: any; totalCount: number }>,
        ) => {
            const { segmentId, data, totalCount } = action.payload;
            state.liquidationRisk[segmentId] = { items: data, totalCount };
        },
        setWalletsFilter: (
            state,
            action: PayloadAction<Partial<Omit<WalletsParams, 'segmentId'>>>,
        ) => {
            state.filters.wallets = {
                ...state.filters.wallets,
                ...action.payload,
            };
        },
        resetWalletsFilter: (state) => {
            state.filters.wallets = {
                ...state.filters.wallets,
                order: 'desc',
                orderBy: 'openValue',
            };
        },
        setModalWindow: (state, action: PayloadAction<any>) => {
            state.modalWindow = {
                ...{
                    modalType: null,
                    isTwitterData: false,
                    xUser: null,
                    benefitModalType: null,
                },
                ...action.payload,
            };
        },
        resetModalWindow: (state) => {
            state.modalWindow = {
                modalType: null,
                isTwitterData: false,
                xUser: null,
                benefitModalType: null,
                authorizationModalType: null,
                wallet: '',
                twitterConnectUrl: '',
                positionId: '',
                editDisplayedNameForFavorites: null,
                vaultInfoModalDetails: null,
            };
        },

        setIsLoadingDisplayX: (state, action: PayloadAction<boolean>) => {
            state.isLoadingDisplayX = action.payload;
        },

        setHeatmapCellDetails: (state, action: PayloadAction<Positions[] | null>) => {
            state.heatmapCellDetails = action.payload;
        },

        setHeatmapSelectedRow: (state, action: PayloadAction<string | null>) => {
            state.heatmapSelectedRow = action.payload;
        },
        setHeatmapSelectedField: (state, action: PayloadAction<number | null>) => {
            state.heatmapSelectedField = action.payload;
        },
        setTempKeyData: (state, action: PayloadAction<any>) => {
            state.tempKeyData = action.payload;
        },
        setConnections: (state, action: PayloadAction<WalletConnection[]>) => {
            state.connections = action.payload;
        },
        updateSingleConnectionLabel: (state, action: PayloadAction<{ address; label }>) => {
            const { address, label } = action.payload;
            state.connections = state.connections.map((connection) => {
                if (connection.address === address) {
                    return { ...connection, privateName: label };
                }
                return connection;
            });
        },
        updateConnection: (state, action: PayloadAction<WalletConnection>) => {
            const { address } = action.payload;
            state.connections = state.connections.map((connection) => {
                if (connection.address === address) {
                    return { ...action.payload };
                }
                return connection;
            });
        },

        setVaults: (state, action: PayloadAction<VaultConnection[]>) => {
            state.vaults = action.payload;
        },

        setFavoriteAccounts: (state, action: PayloadAction<any[]>) => {
            state.favoriteAccounts = action.payload;
        },

        setFavoritePositions: (state, action: PayloadAction<any[]>) => {
            state.favoritePositions = action.payload;
        },

        addToFavoriteAccounts: (state, action: PayloadAction<any>) => {
            state.favoriteAccounts = [...state.favoriteAccounts, action.payload];
            state.favoriteLimits = {
                ...state.favoriteLimits,
                countFavorites: state.favoriteLimits.countFavorites + 1,
            };
        },

        addToFavoritePositions: (state, action: PayloadAction<any>) => {
            state.favoritePositions = [...state.favoritePositions, action.payload];
            state.favoriteLimits = {
                ...state.favoriteLimits,
                countFavorites: state.favoriteLimits.countFavorites + 1,
            };
        },

        updateFavoriteAccount: (state, action) => {
            const index = state.favoriteAccounts?.findIndex(
                (item) => item.address === action.payload.address,
            );

            if (index !== -1) {
                state.favoriteAccounts?.splice(index, 1, action.payload);
            }
        },

        updateFavoritePositions: (state, action) => {
            const index = state.favoritePositions.findIndex(
                (item) => item.id === action.payload.id,
            );

            if (index !== -1) {
                state.favoritePositions.splice(index, 1, action.payload);
            }
        },

        deleteFromFavoriteAccounts: (state, action: PayloadAction<{ address: string }>) => {
            state.featuredAccounts = state.featuredAccounts.filter(
                (item) => item.address !== action.payload.address,
            );

            state.favoriteLimits = {
                ...state.favoriteLimits,
                countFavorites: state.favoriteLimits.countFavorites - 1,
            };

            state.favoriteAccounts = state.favoriteAccounts.filter(
                (item) => item.address !== action.payload.address,
            );
        },

        deleteFromFavoritePositions: (state, action: PayloadAction<{ posId: string }>) => {
            state.featuredPositions = state.featuredPositions.filter(
                (item) => item.id !== action.payload.posId,
            );

            state.favoriteLimits = {
                ...state.favoriteLimits,
                countFavorites: state.favoriteLimits.countFavorites - 1,
            };

            state.favoritePositions = state.favoritePositions.filter(
                (item) => item.id !== action.payload.posId,
            );
        },

        setFavoriteIdsHolderObject: (
            state,
            action: PayloadAction<{
                [key: string]: { id: string; favorite: boolean; openTime: number | null };
            } | null>,
        ) => {
            state.favoriteIdsHolderObject = action.payload;
        },

        updateFavoriteIdsHolderObject: (
            state,
            action: PayloadAction<{
                key: string;
                data: { id: string; favorite: boolean; openTime: number | null };
            }>,
        ) => {
            const { key, data } = action.payload;

            state.favoriteIdsHolderObject = {
                ...state.favoriteIdsHolderObject,
                [key]: data,
            };
        },

        setFeaturedAccounts: (state, action: PayloadAction<any[]>) => {
            state.featuredAccounts = action.payload;
        },

        addToFeaturedAccounts: (state, action: PayloadAction<any>) => {
            const index = state.featuredAccounts.findIndex(
                (item) => item.address === action.payload.address,
            );

            if (index === -1 && action.payload.favorite?.featured) {
                state.featuredAccounts.push(action.payload);
            }
        },

        deleteFromFeaturedAccounts: (state, action: PayloadAction<string>) => {
            state.featuredAccounts = state.featuredAccounts.filter(
                (item) => item.address !== action.payload,
            );
        },

        setFeaturedPositions: (state, action: PayloadAction<any[]>) => {
            state.featuredPositions = action.payload;
        },

        addToFeaturedPositions: (state, action: PayloadAction<any>) => {
            const index = state.featuredPositions.findIndex(
                (item) => item.id === action.payload.id,
            );

            if (index === -1 && action.payload.favorite?.featured) {
                state.featuredPositions.push(action.payload);
            }
        },

        deleteFromFeaturedPositions: (state, action: PayloadAction<string>) => {
            state.featuredPositions = state.featuredPositions.filter(
                (item) => item.id !== action.payload,
            );
        },

        resetFeatured: (state) => {
            state.featuredPositions = [];
            state.featuredAccounts = [];
        },

        setFavoriteLimits: (state, action: PayloadAction<FavoriteLimits>) => {
            state.favoriteLimits = {
                ...action.payload,
                maxFavorites: action.payload.maxFavorites ? action.payload.maxFavorites : Infinity,
            };
        },
        setPerpsCurrentAsset: (state, action: PayloadAction<string>) => {
            state.perpsCurrentAsset = action.payload;
        },
        setPerpsAssetsData: (state, action: PayloadAction<PerpsAssetsData | null>) => {
            state.perpsAssetsData = action.payload;
        },
        addToPositionsToExplore: (state, action: PayloadAction<string>) => {
            if (state.perpsPositionsToExplore.includes(action.payload)) return;
            state.perpsPositionsToExplore.push(action.payload);
        },
        deleteFromPositionsToExplore: (state, action: PayloadAction<string | number>) => {
            state.perpsPositionsToExplore = state.perpsPositionsToExplore.filter(
                (item) => item !== action.payload,
            );
        },
        clearPositionsToExplore: (state) => {
            state.perpsPositionsToExplore = [];
        },
        setPositionExplorerFilters: (
            state,
            action: PayloadAction<Partial<PositionExplorerFilters>>,
        ) => {
            state.positionExplorerFilters = {
                ...state.positionExplorerFilters,
                ...action.payload,
            };
        },
        setLeaderboardsTableFilters: (
            state,
            action: PayloadAction<Partial<LeaderboardsTableFilters>>,
        ) => {
            state.leaderboardsTableFilters = {
                ...state.leaderboardsTableFilters,
                ...action.payload,
            };
        },
        setBuildersDailyAnalytics: (state, action: PayloadAction<Revenue | null>) => {
            state.buildersDailyAnalytics = action.payload;
        },
        setBuildersAllTimeAnalytics: (state, action: PayloadAction<Revenue | null>) => {
            state.buildersAllTimeAnalytics = action.payload;
        },
        setBuildersAllTime: (state, action: PayloadAction<BuildersByPeriod | null>) => {
            state.buildersAllTime = action.payload;
        },
        setBuilders24Hours: (state, action: PayloadAction<BuildersByPeriod | null>) => {
            state.builders24h = action.payload;
        },
        setBuildersByPeriod: (state, action: PayloadAction<BuildersByPeriod | null>) => {
            state.buildersByPeriod = action.payload;
        },
        setTopBuildersRevenue: (state, action: PayloadAction<TopBuildersRevenue | null>) => {
            state.topBuildersRevenue = action.payload;
        },
        setBuilderV2: (state, action: PayloadAction<BuilderV2Personal | null>) => {
            state.builderV2 = action.payload;
        },
        setUsersForBuilder: (state, action: PayloadAction<UsersForBuilder | null>) => {
            state.usersForBuilder = action.payload;
        },
        updateUsersForBuilder: (state, action: PayloadAction<BuilderUser>) => {
            const index = state.usersForBuilder?.items?.findIndex(
                (item) => item.address === action.payload.address,
            );

            if (index && index !== -1 && state.usersForBuilder?.items) {
                state.usersForBuilder?.items.splice(index, 1, action.payload);
            }
        },
        setBuilderMainChartView: (
            state,
            action: PayloadAction<{ key: string; view: BUILDER_CHART_VIEW_TYPES }>,
        ) => {
            state.builderMainChartView = {
                ...state.builderMainChartView,
                [action.payload.key]: action.payload.view,
            };
        },
        setBuildersTableRange: (state, action: PayloadAction<RANGE_TYPES>) => {
            state.buildersTableRange = action.payload;
        },
        setPersonalBuilderRange: (
            state,
            action: PayloadAction<{ key: string; range: RANGE_TYPES }>,
        ) => {
            state.personalBuilderRange = {
                ...state.personalBuilderRange,
                [action.payload.key]: action.payload.range,
            };
        },

        setStatsMetrics: (state, action: PayloadAction<StatsMetrics | null>) => {
            state.statsMetrics = action.payload;
        },

        setStatsExposeRatio: (state, action: PayloadAction<StatsExposeRatio | null>) => {
            state.statsExposeRatio = action.payload;
        },
        setStatsMetricByHype: (state, action: PayloadAction<StatsMetricByHype | null>) => {
            state.statsMetricByHype = action.payload;
        },
        setStatsActivePerpTraders: (
            state,
            action: PayloadAction<StatsActivePerpTraders | null>,
        ) => {
            state.statsActivePerpTraders = action.payload;
        },
        setStatsTotalWalletsOnHL: (state, action: PayloadAction<StatsTotalWalletsOnHL | null>) => {
            state.statsTotalWalletsOnHL = action.payload;
        },
        setStatsWalletCreationRate: (
            state,
            action: PayloadAction<StatsWalletCreationRate | null>,
        ) => {
            state.statsWalletCreationRate = action.payload;
        },
        setStartTotalWalletsEquity: (
            state,
            action: PayloadAction<StartTotalWalletsEquity | null>,
        ) => {
            state.startTotalWalletsEquity = action.payload;
        },
        setStatsEquityDistributionBuckets: (
            state,
            action: PayloadAction<StatsEquityDistributionBuckets | null>,
        ) => {
            state.statsEquityDistributionBuckets = action.payload;
        },
        setLeaderboardsData: (state, action: PayloadAction<HypertrackerLeaderboards>) => {
            if (state.leaderboards === null) {
                state.leaderboards = action.payload;
            } else {
                state.leaderboards = {
                    ...state.leaderboards,
                    ...action.payload,
                };
            }
        },
        updateRowInLeaderboardsTable: (
            state,
            action: PayloadAction<{
                selectedKey: LEADERBOARDS;
                address: string;
                favorite: boolean;
            }>,
        ) => {
            const { selectedKey, address, favorite } = action.payload;

            const updateItem = (
                address,
                favorite,
                leaderboardData: HypertrackerLeaderboardData,
            ) => {
                const updatedRows = (leaderboardData.data || []).map((item) => {
                    if (item.address === address) {
                        const profile: any = item?.profile || {};
                        const profileFavorite = profile?.favorite || {};
                        return {
                            ...item,
                            profile: {
                                ...profile,
                                favorite: {
                                    ...profileFavorite,
                                    favorite,
                                },
                            },
                        };
                    } else {
                        return item;
                    }
                });

                return {
                    ...leaderboardData,
                    data: updatedRows,
                } as HypertrackerLeaderboardData;
            };

            if (state?.leaderboards?.[selectedKey]) {
                switch (selectedKey) {
                    case LEADERBOARDS.PNL_PERP_ONLY:
                    case LEADERBOARDS.ALL_PNL:
                    case LEADERBOARDS.AVG_DAILY_PERP_PNL:
                        state.leaderboards[selectedKey] = updateItem(
                            address,
                            favorite,
                            state.leaderboards[selectedKey] as HypertrackerLeaderboardData,
                        );
                }
            }
        },
        resetLeaderboardsFilters: (state) => {
            state.leaderboardsTableFilters = {
                coin: '',
                limit: 50,
                offset: 0,
                order: 'desc',
                orderBy: 'coin',
                segmentIds: [],
                valueRange: [],
                rankBy: 'pnlAllTime',
            };
        },
    },
});

export const {
    setSegmentsSummary,
    setSegmentsBiasMetrics,
    setSegmentSummaryById,
    setLargestPositions,
    setLiquidationRisk,
    setWallets,
    updateWallets,
    setWalletsFilter,
    resetWalletsFilter,
    setModalWindow,
    resetModalWindow,
    setIsLoadingDisplayX,
    setHeatmapCellDetails,
    setHeatmapSelectedRow,
    setHeatmapSelectedField,
    setTempKeyData,
    setConnections,
    updateConnection,
    updateSingleConnectionLabel,
    setFavoritePositions,
    setFavoriteAccounts,
    addToFavoriteAccounts,
    updateFavoriteAccount,
    addToFavoritePositions,
    updateFavoritePositions,
    deleteFromFavoriteAccounts,
    deleteFromFavoritePositions,
    setFavoriteIdsHolderObject,
    updateFavoriteIdsHolderObject,
    setFeaturedAccounts,
    addToFeaturedAccounts,
    deleteFromFeaturedAccounts,
    setFeaturedPositions,
    addToFeaturedPositions,
    deleteFromFeaturedPositions,
    resetFeatured,
    setFavoriteLimits,
    setPerpsCurrentAsset,
    setPerpsAssetsData,
    addToPositionsToExplore,
    deleteFromPositionsToExplore,
    clearPositionsToExplore,
    setVaults,
    setPositionExplorerFilters,
    setBuildersDailyAnalytics,
    setBuildersAllTimeAnalytics,
    setBuildersAllTime,
    setBuilders24Hours,
    setBuildersByPeriod,
    setTopBuildersRevenue,
    setUsersForBuilder,
    updateUsersForBuilder,
    setBuilderMainChartView,
    setStatsMetrics,
    setStatsExposeRatio,
    setStatsMetricByHype,
    setStatsActivePerpTraders,
    setStatsTotalWalletsOnHL,
    setStatsWalletCreationRate,
    setStartTotalWalletsEquity,
    setStatsEquityDistributionBuckets,
    setBuildersTableRange,
    setPersonalBuilderRange,
    setBuilderV2,
    setLeaderboardsData,
    setLeaderboardsTableFilters,
    updateRowInLeaderboardsTable,
    resetLeaderboardsFilters,
} = slice.actions;

export default slice.reducer;
